import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, Dialog } from "@mui/material";

/**
 * copy per entity from the E5 design; label is "Zone — Number" or "Zone — Number / Network"
 */
export const DEACTIVATE_COPY = {
  zone: {
    title: "Deactivate this Zone?",
    bullets: [
      "Hidden from the active configuration list",
      "Existing schedules and history preserved",
    ],
    label: (zone) => `${zone.zoneName} — ${zone.zoneNumber}`,
  },
  mapping: {
    title: "Deactivate this Mapping?",
    bullets: [
      "Hidden from the active configuration list",
      "Existing configuration and history preserved",
    ],
    label: (zone, network) =>
      `${zone.zoneName} — ${zone.zoneNumber} / ${network.networkName}`,
  },
};

/**
//    * @component  ConfirmDialog
//    * @description Reusable confirmation for destructive actions (E5 Deactivate Zone / Mapping).
//    * Layout, copy slots and colours follow the E5 design "Deactivate confirmation (reusable)".
//    * @returns Confirmation dialog UI
//    */
function ConfirmDialog(props) {
  const {
    open,
    title,
    label,
    bullets,
    error,
    busy,
    onConfirm,
    onCancel,
    confirmLabel,
    busyLabel,
    cancelLabel,
  } = props;

  // keep the last text while the dialog fades out, so it never shows a blank label
  const shownRef = useRef({ title, label, bullets });
  if (open) {
    shownRef.current = { title, label, bullets };
  }
  const shown = shownRef.current;

  // onConfirm fires once per opening, even on double click or held Enter
  const confirmedRef = useRef(false);
  useEffect(() => {
    if (open) {
      confirmedRef.current = false;
    }
  }, [open]);
  // allow a retry after the request finished (e.g. an inline error is shown)
  useEffect(() => {
    if (!busy) {
      confirmedRef.current = false;
    }
  }, [busy]);

  const handleConfirm = () => {
    if (busy || confirmedRef.current) return;
    confirmedRef.current = true;
    onConfirm();
  };

  // Esc cancels; backdrop click does nothing (design); nothing closes while busy
  const handleClose = (event, reason) => {
    if (busy || reason === "backdropClick") return;
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirm-dialog-title"
      PaperProps={{
        role: "alertdialog",
        sx: {
          width: 440,
          maxWidth: "100%",
          borderRadius: "12px",
          boxShadow: "0 20px 48px rgba(16,24,40,.25)",
          p: "22px 22px 18px",
          m: 3,
          boxSizing: "border-box",
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(32,32,32,.4)" } }}
    >
      <Box
        id="confirm-dialog-title"
        sx={{ fontSize: 15, fontWeight: 700, mb: "14px", color: "#202020" }}
      >
        {shown.title}
      </Box>
      <Box sx={{ fontSize: 12, lineHeight: 1.5, color: "#202020", mb: "12px", wordBreak: "break-word" }}>
        <strong>{shown.label}</strong> will be deactivated.
      </Box>
      {shown.bullets && shown.bullets.length > 0 && (
        <Box
          component="ul"
          sx={{ m: "0 0 16px", pl: "22px", fontSize: 12, lineHeight: 1.7, color: "#444" }}
        >
          {shown.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Box>
      )}
      <Box sx={{ fontSize: 12, mb: "22px", color: "#202020" }}>
        Do you want to continue?
      </Box>
      {error && (
        <Box
          role="alert"
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            backgroundColor: "#fef3f2",
            border: "1px solid #fecdca",
            borderRadius: "8px",
            p: "12px 14px",
            mb: "18px",
            fontSize: 11,
            color: "#b42318",
          }}
        >
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#d92d20",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "2px",
            }}
          >
            !
          </Box>
          <span>{error}</span>
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
        <Button
          autoFocus
          onClick={() => !busy && onCancel()}
          disabled={busy}
          disableElevation
          sx={{
            height: 34,
            minWidth: 84,
            px: 3,
            borderRadius: "6px",
            backgroundColor: "#8f8f8f",
            color: "#fff",
            fontSize: 12,
            textTransform: "none",
            "&:hover": { backgroundColor: "#7d7d7d" },
            "&.Mui-disabled": { backgroundColor: "#8f8f8f", color: "#fff", opacity: 0.7 },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={busy}
          sx={{
            height: 34,
            minWidth: 100,
            px: "22px",
            border: "1.5px solid #d92d20",
            borderRadius: "6px",
            backgroundColor: "#fff",
            color: "#b42318",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
            gap: "10px",
            "&:hover": { backgroundColor: "#fef3f2" },
            "&.Mui-disabled": { color: "#b42318", border: "1.5px solid #d92d20", opacity: 0.7 },
          }}
        >
          {busy && (
            <CircularProgress size={16} thickness={5} sx={{ color: "#d92d20" }} />
          )}
          {busy ? busyLabel : confirmLabel}
        </Button>
      </Box>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  bullets: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.string,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
};

ConfirmDialog.defaultProps = {
  bullets: [],
  error: "",
  busy: false,
  confirmLabel: "Deactivate",
  busyLabel: "Deactivating…",
  cancelLabel: "Cancel",
};

export default ConfirmDialog;
